/**
 * What the transport UI knows about the audio element — §4.1's one-owner rule applied
 * to facts that only the element (or the jump in flight) knows.
 *
 * This is deliberately NOT part of `stores/player.ts`: that store is a thin shell over
 * the pure reducer in @mushaf/corpus, and neither buffering nor "which button was
 * pressed" is a decision the reducer makes. Keeping them here means the reducer's tests
 * stay tests of what runs in the browser.
 *
 * WRITERS: the reconciler in `audio/AudioEngine.tsx` for `isBuffering` (A2), and
 *          `lib/commands.ts` → `jumpRecitedPage` for the jump fields.
 * READERS: the transport UI.
 */
import { create } from 'zustand'

interface AudioStatus {
  /**
   * True when the recitation is WANTED but the element cannot yet produce sound —
   * a new surah file fetching after a page jump, or a mid-run stall.
   *
   * False while paused, however cold the element is: a jump taken while paused is
   * silent either way, so there is nothing for the user to wait on (T28).
   */
  isBuffering: boolean

  /**
   * Which of "Recite previous / next page" was pressed, as its delta — only that one
   * wears the spinner. `isBuffering` is the gate, so a stale direction sitting here
   * between jumps renders nothing.
   */
  jumpDirection: number | null

  /**
   * The page a jump is currently heading for, or `null` between jumps.
   *
   * TWO PURPOSES, both from §5.9: it is the base the NEXT press counts from, so two
   * quick presses land two pages on rather than the same page twice (T27); and it is
   * the supersede check, so a newer jump wins if this ever becomes asynchronous.
   */
  jumpTarget: number | null

  setBuffering: (v: boolean) => void
  beginJump: (direction: number, target: number) => void
  /** The jump is silent, so no `isBuffering` will ever arrive to end the spinner (T28). */
  clearJumpDirection: () => void
  endJump: () => void
}

export const useAudioStatus = create<AudioStatus>()((set) => ({
  isBuffering: false,
  jumpDirection: null,
  jumpTarget: null,

  // Guarded so the high-frequency element events (`progress`, `timeupdate`-adjacent
  // ones) cannot wake every subscriber on each fire.
  setBuffering: (v) => set((s) => (s.isBuffering === v ? s : { isBuffering: v })),

  beginJump: (jumpDirection, jumpTarget) => set({ jumpDirection, jumpTarget }),
  clearJumpDirection: () => set({ jumpDirection: null }),
  endJump: () => set({ jumpTarget: null }),
}))

export const selectIsBuffering = (s: AudioStatus) => s.isBuffering

/** True only for the button that was actually pressed, and only while it is loading. */
export const selectJumpBusy = (direction: number) => (s: AudioStatus) =>
  s.isBuffering && s.jumpDirection === direction
